import {Component, OnInit} from '@angular/core';
import {filter, take} from 'rxjs';
import {AuthService} from '../../../_services/auth.service';
import {Company} from '../../../_models/company';
import {EffectiveRole, User} from '../../../_models/user';
import {CompanyWorkspaceService} from '../../../_services/company-workspace.service';
import {GlobalStore} from '../../../global.store';
import {TargetCompanyService} from '../../../_services/target-company.service';
import {UsersService} from '../../../_services/users.service';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-welcome-screen',
  templateUrl: './welcome-screen.component.html',
  styleUrls: ['../auth.scss', './welcome-screen.component.scss'],
  standalone: false
})
export class WelcomeScreenComponent implements OnInit {
  readonly roleLabels: Record<string, string> = {
    [EffectiveRole.ROLE_COMPANY_ADMIN]: 'Admin',
    [EffectiveRole.ROLE_USER]: 'User',
    [EffectiveRole.ROLE_TRADE]: 'Trade'
  };

  loading = true;
  isSelecting = false;
  selectedCompanyId: number | null = null;
  shouldRememberSelection = false;
  currentUser: User | null = null;
  companies: Company[] = [];

  constructor(
    private authService: AuthService,
    private companyWorkspaceService: CompanyWorkspaceService,
    private globalStore: GlobalStore,
    private targetCompanyService: TargetCompanyService,
    private usersService: UsersService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.shouldRememberSelection = this.companyWorkspaceService.isAutoOpenPreferredCompanyEnabled();

    this.globalStore.currentUser$
      .pipe(
        filter((user) => user !== undefined),
        take(1)
      )
      .subscribe((user) => {
        if (!user) {
          this.loading = false;
          this.authService.logout(true);
          return;
        }

        this.usersService.getCurrentUserCompanies().pipe(take(1)).subscribe({
          next: (companies) => {
            const hydratedUser: User = {
              ...user,
              companies
            };

            this.currentUser = hydratedUser;
            this.loading = false;
            const shouldShowCompanies = this.route.snapshot.queryParamMap.get('showCompanies') === 'true';

            if (!this.companyWorkspaceService.isAppWorkspaceHost()) {
              this.companyWorkspaceService.navigateToDestination(
                this.companyWorkspaceService.resolvePostLoginDestination(hydratedUser)
              );
              return;
            }

            if (!shouldShowCompanies && !this.companyWorkspaceService.shouldShowWelcomeScreen(hydratedUser)) {
              this.companyWorkspaceService.navigateToDestination(
                this.companyWorkspaceService.resolvePostLoginDestination(hydratedUser)
              );
              return;
            }

            const preferredCompany = this.companyWorkspaceService.resolvePreferredCompany(hydratedUser);
            if (!shouldShowCompanies && preferredCompany) {
              this.targetCompanyService.setTargetCompanyId(preferredCompany.id);
              this.navigateToCompanyDestination(preferredCompany);
              return;
            }

            if (this.openOnlyAvailableCompany(companies)) {
              return;
            }

            this.companyWorkspaceService.clearCurrentCompanyContext();
            this.globalStore.resetState();
            this.companies = [...companies];
          },
          error: () => {
            this.companyWorkspaceService.clearCurrentCompanyContext();
            this.globalStore.resetState();
            this.currentUser = user;
            this.loading = false;
            const fallbackCompanies = user.companies ?? [];

            if (this.openOnlyAvailableCompany(fallbackCompanies)) {
              return;
            }

            this.companies = [...fallbackCompanies];
          }
        });
      });
  }

  get userName(): string {
    return this.currentUser?.name || this.currentUser?.email || 'User';
  }

  get hasCompanies(): boolean {
    return this.companies.length > 0;
  }

  get contentClassName(): string {
    return this.hasCompanies ? 'welcome-screen__content--companies' : 'welcome-screen__content--empty';
  }

  get isScrollableTable(): boolean {
    return this.companies.length >= 6;
  }

  get skeletonRows(): number[] {
    return Array.from({length: 6}, (_, index) => index);
  }

  trackByCompanyId(_: number, company: Company): number {
    return company.id;
  }

  getProjectsLabel(projectsCount: number | null | undefined): string {
    const normalizedProjectsCount = projectsCount ?? 0;
    return `${normalizedProjectsCount} projects`;
  }

  getRoleLabel(company: Company): string {
    return this.roleLabels[company.companyRole] ?? '-';
  }

  getRoleClass(company: Company): string {
    switch (company.companyRole) {
      case EffectiveRole.ROLE_COMPANY_ADMIN:
        return 'welcome-screen__role--admin';
      case EffectiveRole.ROLE_TRADE:
        return 'welcome-screen__role--trade';
      default:
        return 'welcome-screen__role--user';
    }
  }

  onSelectCompany(company: Company): void {
    if (!company?.id || this.isSelecting) {
      return;
    }

    this.isSelecting = true;
    this.selectedCompanyId = company.id;
    this.targetCompanyService.setTargetCompanyId(company.id);

    if (this.shouldRememberSelection) {
      this.companyWorkspaceService.savePreferredCompany(company.id);
    } else {
      this.companyWorkspaceService.clearPreferredCompany();
    }

    this.navigateToCompanyDestination(company);
  }

  onLogout(): void {
    this.authService.logout();
  }

  private navigateToCompanyDestination(company: Company): void {
    const destination = this.companyWorkspaceService.getCompanySwitcherDestination(company);

    if (destination.external) {
      window.location.replace(destination.url);
      return;
    }

    this.companyWorkspaceService.navigateToDestination(destination);
  }

  private openOnlyAvailableCompany(companies: Company[]): boolean {
    if (companies.length !== 1) {
      return false;
    }

    const [company] = companies;
    if (!company?.id) {
      return false;
    }

    this.isSelecting = true;
    this.selectedCompanyId = company.id;
    this.companyWorkspaceService.rememberLastCompany(company.id);
    this.navigateToCompanyDestination(company);
    return true;
  }
}
